import { DataResult, process, State } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import {
  Grid,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords,
  GridToolbar,
  GridGroupExpandChangeEvent,
  GridCustomCellProps
} from '@progress/kendo-react-grid';
import { Col, Row } from 'react-bootstrap';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { PrivatePage } from '../../../../components/private-page';
import Toolbar from '../../../game-designer/toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { useCallback, useEffect, useState } from 'react';
import { NoRecords } from '../../../../components/grid/no-records';
import { appStore } from '../../../../stores/app-store';
import {
  AdminAddDisplayLanguageContentAsync,
  AdminDisplayLanguageDocumentResponse,
  AdminDisplayLanguageResponse,
  AdminUpdateDisplayLanguageAsync,
  AdminUpdateDisplayLanguageDocumentAsync,
  DisplayLanguage,
  DisplayLanguageList,
  DisplayLanguageResource,
  GetAdminDisplayLanguageAsync,
  GetAdminDisplayLanguageLatestContentAsync,
  GetAdminDisplayLanguageLatestDocumentAsync,
  GetAllAdminDisplayLanguageDefaultValuesAsync
} from '../../../../services/admin-display-language';
import StandardInput from '../../../../components/forms/standard-input';
import { Label } from '@progress/kendo-react-labels';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { useParams } from 'react-router-dom';
import { GetUpdatedVersionAsync } from '../../../../utils/game-document';
import DisplayLanguageIdShowNote from '../../../../constants/display-language';
import {
  setExpandedState,
  setGroupIds
} from '@progress/kendo-react-data-tools';
import { useToast } from '../../../../contexts/toast-context';

type selectedResource = {
  isOriginal?: boolean;
  resourceId?: string;
  type?: string;
  value?: string;
};

export default function DisplayLanguageEdit() {
  const initialDataState: State = {
    ...DefaultGridSettings.initialDataState,
    take: 2000,
    skip: 0
  };

  const { id } = useParams();

  const [libraries, setLibraries] = useState<DataResult>({
    data: [],
    total: 0
  });

  const [displayLanguage, setDisplayLanguage] =
    useState<AdminDisplayLanguageResponse>();

  const [displayLanguageDocument, setDisplayLanguageDocument] =
    useState<AdminDisplayLanguageDocumentResponse>();

  const [displayLanguageContent, setDisplayLanguageContent] =
    useState<DisplayLanguage>();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [collapsedState, setCollapsedState] = useState<string[]>([]);
  const { onToastOpen } = useToast();

  const updateResourceValue = (resourceId: string, newValue: string) => {
    let newDisplayLanguage = { ...displayLanguageContent };
    let resourceExists = false;
    let updatedResources = (displayLanguageContent?.resources ?? []).map(
      (resource) => {
        if (resource.id === resourceId) {
          resourceExists = true;
          return { ...resource, value: newValue };
        }
        return resource;
      }
    );

    if (!resourceExists) {
      const newResource: DisplayLanguageResource = {
        id: resourceId,
        name: '',
        description: '',
        value: newValue,
        type: 'text'
      };
      updatedResources.push(newResource);
    }

    newDisplayLanguage.resources = updatedResources;

    setDisplayLanguageContent(newDisplayLanguage);
  };

  const CustomCellInput = ({
    dataItem,
    ...props
  }: { dataItem: any } & Record<string, any>) => {
    const initialValue =
      displayLanguageContent?.resources?.find((x) => x.id === dataItem.id)
        ?.value ?? '';
    const [textState, setTextState] = useState<string>(initialValue);
    const showNote =
      DisplayLanguageIdShowNote.find((i) => i === dataItem.id) !== undefined;

    return (
      <div className={'d-column w-full p-2'}>
        <StandardInput
          name={'text'}
          value={textState}
          onChange={(e: InputChangeEvent) =>
            setTextState(e.target.value!.toString())
          }
          onBlur={() => updateResourceValue(dataItem.id, textState)}
        />
        {showNote && (
          <Label className={'display-language-note'}>
            When filling in this field, be sure to include the % sign to display
            the value correctly.
          </Label>
        )}
      </div>
    );
  };

  const CustomCell = (props: GridCustomCellProps) => {
    if (!props.dataItem.id) {
      return <td {...props.tdProps}></td>;
    }
    return (
      <td {...props.tdProps}>
        <CustomCellInput dataItem={props.dataItem} />
      </td>
    );
  };

  const processWithGroups = (data: DisplayLanguageList[], dataState: State) => {
    dataState.group = [{ field: 'activityType' }];
    const newDataState = process(data, dataState);

    setGroupIds({ data: newDataState.data, group: dataState.group });

    newDataState.data = setExpandedState({
      data: newDataState.data,
      collapsedIds: collapsedState
    });

    newDataState.data = newDataState.data.sort((item) =>
      item.groupId === 'GameactivityType' ? -1 : 1
    );

    return newDataState;
  };

  const onSubmit = async ({ isPublished = false }) => {
    try {
      let newVersion = await GetUpdatedVersionAsync(
        'patch',
        displayLanguage!.version!.replace('v.', '')
      );

      newVersion = `v.${newVersion}`;

      const language = await AdminUpdateDisplayLanguageAsync(parseInt(id!), {
        languageCode: displayLanguage?.languageCode,
        description: displayLanguage?.description
      });

      const document = await AdminUpdateDisplayLanguageDocumentAsync(
        language.id!,
        displayLanguageDocument!.id!,
        {
          documentStatus: isPublished
            ? 'Published'
            : (displayLanguageDocument!.status as string),
          version: newVersion,
          fileName: `display-language-${language.id}-${newVersion}.json`
        }
      );

      await AdminAddDisplayLanguageContentAsync(
        language.id,
        document.id,
        displayLanguageContent
      );

      onToastOpen({
        title: 'Display language',
        body: <div>Language updated successfully.</div>,
        style: 'success'
      });

      fetchLibrary();
    } catch (error) {
      console.error('Failed to update language:', error);
      onToastOpen({
        title: 'Display language',
        body: <div>Failed to update language.</div>,
        style: 'error'
      });
    }
  };

  const fetchLibrary = useCallback(async () => {
    try {
      appStore.showLoading();
      const { group, ...apiState } = dataState;
      const result =
        await GetAllAdminDisplayLanguageDefaultValuesAsync(apiState);

      const displayLanguage = await GetAdminDisplayLanguageAsync(
        parseInt(id!),
        parseInt(id!)
      );

      const document = await GetAdminDisplayLanguageLatestDocumentAsync(
        parseInt(id!)
      );

      const content = await GetAdminDisplayLanguageLatestContentAsync(
        parseInt(id!),
        apiState
      );

      const data = result.data
        .filter(
          (item) =>
            item.activityType !== '' && item.activityType !== 'Assessment'
        )
        .sort((item) =>
          item.activityType === 'Game' ? -1 : 1
        ) as DisplayLanguageList[];

      result.data = data;
      setLibraries(result);
      setDisplayLanguage(displayLanguage);
      setDisplayLanguageDocument(document);
      setDisplayLanguageContent(content);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    } finally {
      appStore.hideLoading();
    }
  }, []);

  const onExpandChange = useCallback((event: GridGroupExpandChangeEvent) => {
    setCollapsedState(
      event.groupExpand.filter((g) => !g.expanded).map((g) => g.value as string)
    );
  }, []);

  useEffect(() => {
    fetchLibrary();
  }, [id]);

  return (
    <PrivatePage
      breadcrumb={[
        { label: 'Dashboard', href: 'dashboard' },
        { label: 'Administration', href: '../administration' },

        {
          label: 'Languages Management',
          href: 'administration/languages/manage'
        }
      ]}
      pageTitle={'Administration Language'}>
      <>
        <Toolbar
          title={'Display Language'}
          notificationTitle={'Display Language'}
          showDefaultContent={false}>
          <Button
            themeColor={'primary'}
            className={'me-1'}
            onClick={() => onSubmit({ isPublished: false })}>
            Save
          </Button>
          <Button
            themeColor={'warning'}
            className={'me-1'}
            onClick={() => onSubmit({ isPublished: true })}>
            Publish
          </Button>
        </Toolbar>
        <div className={'pt-2'}>
          <GridToolBar
            searchPlaceholder={'Search text'}
            columnsToSearch={['value']}
            showCardMode={false}
            {...dataState}
            onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}></GridToolBar>

          <Grid
            className={'grid-language-edit w-full'}
            data={processWithGroups(libraries?.data ?? [], dataState)}
            dataItemKey={'id'}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            onGroupExpandChange={onExpandChange}>
            <GridToolbar>
              <Row
                className={
                  'd-flex justify-content-between w-100 align-items-center'
                }>
                <Col>
                  <span className={'fw-bold'}>Original</span>
                </Col>
                <Col>
                  <div className="d-flex justify-content-between gap-2">
                    <span className={'fw-bold'}>
                      {' '}
                      {displayLanguage?.languageName}
                    </span>
                  </div>
                </Col>
              </Row>
            </GridToolbar>
            <Column
              field={'value'}
              className="w-50"
              editable={false}
              headerClassName={'d-none'}
            />
            <Column
              className="w-50"
              editable={false}
              headerClassName={'d-none'}
              cells={{ data: CustomCell }}
            />

            <GridNoRecords>
              <NoRecords />
            </GridNoRecords>
          </Grid>
        </div>
      </>
    </PrivatePage>
  );
}
